import type { MarketingTrackingInput, MarketingTrackingView } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, get, put } from "../../api";
import { Alert, Badge, Button, Checkbox, Field, Loading, Panel, PanelHeader, formatDateTime } from "../../components/ui";

/**
 * Settings → Marketing tracking: Google Analytics and Facebook on the public booking pages (never the
 * staff app). Conversions are sent from the server when a booking goes live: its reference and total.
 * Services and matching details only when switched on (both start off).
 */
export function MarketingTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["marketing-tracking"], queryFn: () => get<MarketingTrackingView>("/api/marketing-tracking") });
  const [v, setV] = useState<MarketingTrackingInput | null>(null);
  useEffect(() => {
    if (q.data && !v)
      setV({
        enabled: q.data.enabled,
        googleMeasurementId: q.data.googleMeasurementId,
        facebookPixelId: q.data.facebookPixelId,
        googleApiSecret: "",
        facebookAccessToken: "",
        includeServices: q.data.includeServices,
        sendMatching: q.data.sendMatching,
      });
  }, [q.data, v]);
  const save = useMutation({
    mutationFn: () => put<MarketingTrackingView>("/api/marketing-tracking", v),
    onSuccess: (data) => {
      qc.setQueryData(["marketing-tracking"], data);
      setV((cur) => (cur ? { ...cur, googleApiSecret: "", facebookAccessToken: "" } : cur));
    },
  });
  if (q.isLoading || !v) return q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Loading />;
  const d = q.data!;
  const set = (patch: Partial<MarketingTrackingInput>) => setV({ ...v, ...patch });

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Panel>
        <PanelHeader
          title="Marketing tracking"
          description="Google Analytics and Facebook ads on the public booking pages only (never the staff app): page views and “started booking” from the page, and each booking as a conversion (its reference and total) sent from Vaulted when it's confirmed. No names, birthdays, or anything clinical."
          action={d.enabled ? <Badge tone="ok">On</Badge> : <Badge tone="off">Off</Badge>}
        />
        <form
          className="flex flex-col gap-4 px-5 py-4 text-sm"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Checkbox label="Turn tracking on" checked={v.enabled} onChange={(on) => set({ enabled: on })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Google measurement ID" placeholder="G-…" value={v.googleMeasurementId ?? ""} onChange={(e) => set({ googleMeasurementId: e.target.value.trim() || null })} />
            <Field
              label="Google API secret"
              type="password"
              autoComplete="off"
              placeholder={d.hasGoogleApiSecret ? "Saved (type to replace)" : "Measurement Protocol API secret"}
              value={v.googleApiSecret}
              onChange={(e) => set({ googleApiSecret: e.target.value })}
            />
            <Field label="Facebook pixel ID" placeholder="Dataset / pixel ID" value={v.facebookPixelId ?? ""} onChange={(e) => set({ facebookPixelId: e.target.value.trim() || null })} />
            <Field
              label="Facebook access token"
              type="password"
              autoComplete="off"
              placeholder={d.hasFacebookAccessToken ? "Saved (type to replace)" : "Conversions API access token"}
              value={v.facebookAccessToken}
              onChange={(e) => set({ facebookAccessToken: e.target.value })}
            />
          </div>
          <p className="text-xs text-muted">The secret keys are kept encrypted and never shown again. Paste them here only, never in an email, text, or chat.</p>

          <div className="flex flex-col gap-2 rounded border border-line p-3">
            <p className="font-semibold">What's shared with each conversion</p>
            <p className="text-ink-soft">Always: the booking reference, the total, and Google's and Facebook's own cookies from the booking page (their anonymous visitor and ad-click IDs).</p>
            <Checkbox label="Also send the services booked (names)" checked={v.includeServices} onChange={(on) => set({ includeServices: on })} />
            <Checkbox label="Also send matching details: hashed email and phone, IP address, and browser" checked={v.sendMatching} onChange={(on) => set({ sendMatching: on })} />
            <p className="text-xs text-wait">
              Both are off unless your attorney has said they're fine to send. The platforms can match a hashed email or phone to the person, and a service name can be health information.
            </p>
          </div>

          {save.error && <Alert>{errorMessage(save.error)}</Alert>}
          {save.isSuccess && <Alert tone="ok">Saved.</Alert>}
          <div>
            <Button type="submit" variant="primary" busy={save.isPending}>
              Save
            </Button>
          </div>
        </form>
      </Panel>

      <Panel>
        <PanelHeader title="Recent conversions" description="What Vaulted sent, newest first. Sent ones keep only the booking reference." />
        {d.recent.length ? (
          <ul className="divide-y divide-mist px-5 text-sm">
            {d.recent.map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
                <span className="font-semibold capitalize">{e.destination}</span>
                <span>{e.event}</span>
                <span className="text-muted">Booking {e.reference}</span>
                <span className="text-muted">{formatDateTime(e.at)}</span>
                {e.status === "sent" ? <Badge tone="ok">Sent</Badge> : e.status === "queued" ? <Badge tone="wait">Waiting</Badge> : <Badge tone="off">Not sent</Badge>}
                {e.error && <span className="basis-full text-danger">{e.error}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-5 py-3 text-sm text-muted">None yet.</p>
        )}
      </Panel>
    </div>
  );
}
